import dayjs from 'dayjs';

import { Project, ProjectMemberRole } from '@graphql/generated';
import useMe from '@hooks/useMe';

type ProjectMembership = {
  isMember: boolean;
  isViewer: boolean;
  isEditor: boolean;
  isAdmin: boolean;
  isOwner: boolean;
  isMemberOrOwner: boolean;
  isAdminOrHigher: boolean;
  isEditorOrHigher: boolean;
  isTeamExpired: boolean;
};

export function getProjectMembership(
  project:
    | Pick<Project, 'members' | 'creator' | 'currentUserRole' | 'team'>
    | undefined,
  meId: string
): ProjectMembership {
  if (!project) {
    return {
      isMember: false,
      isViewer: false,
      isEditor: false,
      isAdmin: false,
      isOwner: false,
      isMemberOrOwner: false,
      isAdminOrHigher: false,
      isEditorOrHigher: false,
      isTeamExpired: false,
    };
  }

  const { members, currentUserRole, team } = project;

  // members[].user can be null for a team-visible project the user isn't an
  // explicit member of (TA-10106); codegen still types it non-null (TA-10297).
  const isMember = members?.some((u) => u.user?.id === meId) ?? false;
  const isOwner = currentUserRole === ProjectMemberRole.Owner;
  const isAdmin = currentUserRole === ProjectMemberRole.Admin;
  const isEditor = currentUserRole === ProjectMemberRole.Editor;
  const isViewer = currentUserRole === ProjectMemberRole.Viewer;
  const isMemberOrOwner = isMember || isOwner;
  const isAdminOrHigher =
    currentUserRole === ProjectMemberRole.Owner ||
    currentUserRole === ProjectMemberRole.Admin;
  const isEditorOrHigher =
    currentUserRole === ProjectMemberRole.Editor || isAdminOrHigher;

  const isTeamExpired =
    !(team?.personal ?? false) &&
    (!(
      team?.subscriptionDetails?.isSubscriptionActive ||
      team?.subscriptionDetails?.subscriptionStatus === 'active'
    ) ||
      dayjs().isAfter(dayjs(team?.subscriptionDetails?.expirationDate)));

  return {
    isMember,
    isViewer,
    isEditor,
    isAdmin,
    isOwner,
    isMemberOrOwner,
    isAdminOrHigher,
    isEditorOrHigher,
    isTeamExpired,
  };
}

export const useProjectMembership = (
  project?: Pick<Project, 'members' | 'creator' | 'currentUserRole' | 'team'>
): ProjectMembership => {
  const { me } = useMe();
  return getProjectMembership(project, me?.id ?? '');
};
